import { toast } from "sonner";
import { useState, useContext, useEffect } from "react";
import { trpc } from "../../../trpc";
import MerchantContext from "../../../contexts/MerchantContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Loader2 } from "lucide-react";

type SoftinmAuthorizationMedia = {
  id: number;
  descripcion: string;
};

type SoftinmGlobalVariablesConfigurationProps = {
  crmConnection: any;
};

const SoftinmGlobalVariablesConfiguration = ({
  crmConnection,
}: SoftinmGlobalVariablesConfigurationProps): JSX.Element => {
  const { merchantId } = useContext(MerchantContext);

  const [selectedIdMedioAut, setSelectedIdMedioAut] = useState<number | undefined>(
    crmConnection?.softinm?.idMedioAut
  );
  const [availableMedia, setAvailableMedia] = useState<SoftinmAuthorizationMedia[]>([]);
  const [isLoadingMedia, setIsLoadingMedia] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const utils = trpc.useUtils();

  const getAuthorizationMedia = trpc.crmSelfConfig.getSoftinmAuthorizationMedia.useMutation({
    onSuccess: (result) => {
      if (result.success && result.data) {
        setAvailableMedia(result.data);
        setIsLoadingMedia(false);
      } else if (!result.success) {
        toast.error("Error", {
          description: result.error || "No se pudieron obtener los medios de autorización.",
        });
        setIsLoadingMedia(false);
      }
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al obtener los medios de autorización.",
      });
      setIsLoadingMedia(false);
    },
  });

  const updateSoftinmConnection = trpc.crmSelfConfig.updateSoftinm.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.get.invalidate({ merchantId: merchantId || "" });
      toast.success("Configuración guardada", {
        description: "Las variables globales se han guardado correctamente.",
      });
      setIsSaving(false);
      setIsEditing(false);
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al guardar las variables globales.",
      });
      setIsSaving(false);
    },
  });

  useEffect(() => {
    if (
      crmConnection?.provider === "softinm" &&
      crmConnection?.softinm?.apiKey &&
      crmConnection?.softinm?.companyName &&
      availableMedia.length === 0 &&
      !isLoadingMedia &&
      isEditing
    ) {
      setIsLoadingMedia(true);
      getAuthorizationMedia.mutate({
        apiKey: crmConnection.softinm.apiKey,
        companyName: crmConnection.softinm.companyName,
      });
    }
  }, [
    crmConnection?.provider,
    crmConnection?.softinm?.apiKey,
    crmConnection?.softinm?.companyName,
    availableMedia.length,
    isLoadingMedia,
    isEditing,
    getAuthorizationMedia,
  ]);

  useEffect(() => {
    setSelectedIdMedioAut(crmConnection?.softinm?.idMedioAut);
  }, [crmConnection?.softinm?.idMedioAut]);

  const handleIdMedioAutChange = (value: string) => {
    setSelectedIdMedioAut(value === "" ? undefined : Number(value));
  };

  const handleSave = () => {
    if (!merchantId || !crmConnection?.id) return;

    setIsSaving(true);
    updateSoftinmConnection.mutate({
      merchantId,
      id: crmConnection.id,
      idMedioAut: selectedIdMedioAut,
    });
  };

  const hasConfiguration = () => {
    return !!(crmConnection?.softinm?.idMedioAut && crmConnection.softinm.idMedioAut > 0);
  };

  const getMediaName = (mediaId: number | undefined): string => {
    if (!mediaId) return "";
    const media = availableMedia.find((m) => m.id === mediaId);
    return media?.descripcion || "";
  };

  if (crmConnection?.provider !== "softinm") {
    return <></>;
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-lg font-bold mb-2">
              Variables Globales
            </p>
            <p className="text-sm text-gray-600">
              Configura el medio de autorización para el manejo de datos personales.
            </p>
          </div>
          <div className="flex flex-row gap-2">
            {hasConfiguration() ? (
              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                Configurado
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">
                Sin configurar
              </span>
            )}
            {!isEditing && (
              <Button
                variant="main"
                size="sm"
                onClick={() => {
                  setIsEditing(true);
                  // Load authorization media if not already loaded
                  if (
                    availableMedia.length === 0 &&
                    crmConnection?.softinm?.apiKey &&
                    crmConnection?.softinm?.companyName
                  ) {
                    setIsLoadingMedia(true);
                    getAuthorizationMedia.mutate({
                      apiKey: crmConnection.softinm.apiKey,
                      companyName: crmConnection.softinm.companyName,
                    });
                  }
                }}
              >
                Editar
              </Button>
            )}
          </div>
        </div>

        {isEditing ? (
          <>
            {isLoadingMedia ? (
              <div className="flex flex-row gap-2">
                <Spinner size="sm" />
                <p className="text-sm text-gray-500">
                  Cargando medios de autorización...
                </p>
              </div>
            ) : availableMedia.length === 0 ? (
              <p className="text-sm text-gray-500">
                No se pudieron cargar los medios de autorización.
              </p>
            ) : (
              <div className="flex flex-col gap-4">
                <div>
                  <Label className="text-sm font-semibold text-gray-700">
                    Medio de autorización de datos personales
                  </Label>
                  <select
                    value={selectedIdMedioAut !== undefined ? String(selectedIdMedioAut) : ""}
                    onChange={(e) => handleIdMedioAutChange(e.target.value)}
                    className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm hover:border-gray-300 focus:border-purple-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-purple-500"
                  >
                    <option value="">Ninguno</option>
                    {availableMedia.map((media) => (
                      <option key={media.id} value={media.id}>
                        {media.descripcion}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-4 flex flex-row gap-2">
                  <Button
                    variant="main"
                    size="sm"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving && <Loader2 className="size-4 animate-spin" />}
                    Guardar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setIsEditing(false);
                      // Reset to saved values
                      setSelectedIdMedioAut(crmConnection?.softinm?.idMedioAut);
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col gap-2">
            {hasConfiguration() ? (
              <div className="flex items-center justify-between text-xs">
                <p className="font-medium text-gray-600">
                  Medio de autorización de datos personales:
                </p>
                <p className="text-gray-700">
                  {isLoadingMedia || availableMedia.length === 0
                    ? `ID: ${crmConnection?.softinm?.idMedioAut}`
                    : getMediaName(crmConnection?.softinm?.idMedioAut) ||
                      `ID: ${crmConnection?.softinm?.idMedioAut}`}
                </p>
              </div>
            ) : (
              <p className="text-xs text-gray-500">
                Haz clic en Editar para configurar las variables globales.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SoftinmGlobalVariablesConfiguration;
